import type {OperationVariables} from "@apollo/client"
import type {MutationOptions} from "@apollo/client/core"
import {EmptyObject} from "@common/helpers/api/fetch"
import {firstValueFrom, type Observable} from "rxjs"
import type {MutationResult} from "apollo-angular"

// Callers never pass `mutation` (the angular service provides it), so omit it
type MutateOptions<ResultType, VariablesType extends OperationVariables> = Omit<MutationOptions<ResultType, VariablesType>, "mutation">

const hasOnlyExpectedErrors = (errors: readonly {extensions?: Record<string, unknown>}[] | undefined): boolean =>
    !!errors && errors.length > 0 && errors.every((e) => e.extensions?.["expected"])

export const mutateIgnoringErrors =
    <ResultType = object, VariablesType extends OperationVariables = EmptyObject>(mutation: {
        mutate: (variables?: VariablesType, options?: MutateOptions<ResultType, VariablesType>) => Observable<MutationResult<ResultType>>
    }) =>
    (variables?: VariablesType, options?: MutateOptions<ResultType, VariablesType>): Promise<ResultType | null | undefined> =>
        firstValueFrom(mutation.mutate(variables, {...options, errorPolicy: "all"})).then(({data}) => data)

export const mutateThrowingErrors =
    <ResultType = object, VariablesType extends OperationVariables = EmptyObject>(mutation: {
        mutate: (variables?: VariablesType, options?: MutateOptions<ResultType, VariablesType>) => Observable<MutationResult<ResultType>>
    }) =>
    (variables?: VariablesType, options?: MutateOptions<ResultType, VariablesType>): Promise<ResultType> =>
        firstValueFrom(mutation.mutate(variables, {...options, errorPolicy: "all"})).then(({data, errors}) => {
            if (errors?.length && !hasOnlyExpectedErrors(errors)) {
                throw errors[0]
            }
            return data!
        })

export const mutateReturningDataAndErrors =
    <ResultType = object, VariablesType extends OperationVariables = EmptyObject>(mutation: {
        mutate: (variables?: VariablesType, options?: MutateOptions<ResultType, VariablesType>) => Observable<MutationResult<ResultType>>
    }) =>
    (variables?: VariablesType, options?: MutateOptions<ResultType, VariablesType>): Promise<MutationResult<ResultType>> =>
        firstValueFrom(mutation.mutate(variables, {...options, errorPolicy: "all"}))
